import { createContext, useContext, useState, useEffect, ReactNode, Dispatch, SetStateAction } from "react";
import { PaymentMethod } from "@appTypes/index";
import { SUBSCRIPTION_SOURCES } from "@constants/subscriptions";
import { useSubscriptionContext } from "@context/SubscriptionContext";
import getPaymentMethods from "@api/getPaymentMethods";
import { isPaymentMethodExpired } from "@utils/getPaymentMethodDisplayInformation";

export const sortPaymentMethods = (methods: PaymentMethod[]): PaymentMethod[] =>
  [...methods].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;

    const aExpired = isPaymentMethodExpired(a);
    const bExpired = isPaymentMethodExpired(b);
    if (aExpired && !bExpired) return 1;
    if (!aExpired && bExpired) return -1;

    return 0;
  });

export interface PaymentMethodContextType {
  paymentMethods: PaymentMethod[];
  arePaymentMethodsLoading: boolean;
  error: string | null;
  hasZuoraSub: boolean;
  setPaymentMethods: Dispatch<SetStateAction<PaymentMethod[]>>;
  refresh: () => void;
}

const defaultContext: PaymentMethodContextType = {
  paymentMethods: [],
  arePaymentMethodsLoading: true,
  error: null,
  hasZuoraSub: false,
  setPaymentMethods: () => undefined,
  refresh: () => undefined,
};

export const PaymentMethodContext = createContext<PaymentMethodContextType>(defaultContext);

export const PaymentMethodContextProvider = ({ children }: { children: ReactNode }) => {
  const { subscriptions, areSubscriptionsLoading } = useSubscriptionContext();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [arePaymentMethodsLoading, setArePaymentMethodsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const hasZuoraSub = subscriptions.some((s) => s.source === SUBSCRIPTION_SOURCES.ZUORA);

  useEffect(() => {
    if (areSubscriptionsLoading) return;

    if (!hasZuoraSub) {
      setArePaymentMethodsLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchPaymentMethods = async () => {
      setArePaymentMethodsLoading(true);
      setError(null);

      const response = await getPaymentMethods(controller.signal);
      if (response.error === "AbortError") return;

      if (response.success && response.data) {
        setPaymentMethods(sortPaymentMethods(response.data));
      } else {
        setError(response.error);
      }
      setArePaymentMethodsLoading(false);
    };

    fetchPaymentMethods();
    return () => controller.abort();
  }, [areSubscriptionsLoading, hasZuoraSub, refreshToken]);

  const refresh = () => setRefreshToken((prev) => prev + 1);

  return (
    <PaymentMethodContext.Provider
      value={{
        paymentMethods,
        arePaymentMethodsLoading,
        error,
        hasZuoraSub,
        setPaymentMethods,
        refresh,
      }}
    >
      {children}
    </PaymentMethodContext.Provider>
  );
};

export const usePaymentMethodContext = () => useContext(PaymentMethodContext);
